import React, { useEffect, useState } from 'react';
import { Box, Button, Modal, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { toggleLoader } from '../../store/loader/loaderReducer';
import { getVisibility, updateVisibility, VISIBILITY } from '../../Api/VisibilityApi';
import VisibilitySelector from './VisibilitySelector';
import styles from './VisibilityModal.module.css';

/**
 * Change the visibility of one existing resource.
 *
 * Used by the four listing pages. Only the owner can save : the backend refuses
 * the change otherwise, and the form is shown read only in that case.
 */
const VisibilityModal = ({ open, onClose, orgId, resourceType, resourceId, resourceName, onSaved }) => {
  const dispatch = useDispatch();

  const [visibility, setVisibility] = useState(VISIBILITY.PUBLIC);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isOwner, setIsOwner] = useState(true);
  const [ownerId, setOwnerId] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || !resourceId || !orgId) return;

    setLoaded(false);
    dispatch(toggleLoader(true));

    getVisibility(
      { org_id: orgId, resource_type: resourceType, resource_id: resourceId },
      (res) => {
        const data = res?.data || {};
        setVisibility(data.visibility ?? VISIBILITY.PUBLIC);
        setSelectedUserIds(data.selected_user_ids || []);
        setIsOwner(data.is_owner !== false);
        setOwnerId(data.owner_id ?? null);
        setLoaded(true);
        dispatch(toggleLoader(false));
      },
      (err) => {
        dispatch(toggleLoader(false));
        toast.error(err?.response?.data?.message || err.message);
        onClose();
      }
    );
  }, [open, resourceId, resourceType, orgId]);

  const handleSave = () => {
    if (visibility === VISIBILITY.PRIVATE_SELECTED_USERS && selectedUserIds.length === 0) {
      toast.error('Select at least one user, or switch to "Only me".');
      return;
    }

    dispatch(toggleLoader(true));

    updateVisibility(
      {
        org_id: orgId,
        resource_type: resourceType,
        resource_id: resourceId,
        visibility,
        selected_user_ids: selectedUserIds,
      },
      () => {
        dispatch(toggleLoader(false));
        toast.success('Visibility updated');
        onClose();
        if (onSaved) onSaved();
      },
      (err) => {
        dispatch(toggleLoader(false));
        toast.error(err?.response?.data?.message || err.message);
      }
    );
  };

  return (
    <Modal open={!!open} onClose={onClose}>
      <Box className={styles.modal}>
        <Typography className={styles.title}>Visibility</Typography>
        {resourceName && <Typography className={styles.subtitle}>{resourceName}</Typography>}

        {loaded && (
          <>
            <VisibilitySelector
              visibility={visibility}
              selectedUserIds={selectedUserIds}
              disabled={!isOwner}
              ownerId={ownerId}
              onChange={({ visibility: nextVisibility, selectedUserIds: nextUserIds }) => {
                setVisibility(nextVisibility);
                setSelectedUserIds(nextUserIds);
              }}
            />

            {!isOwner && (
              <Typography className={styles.readonly}>
                Only the owner can change the visibility of this item.
              </Typography>
            )}

            <Box className={styles.actions}>
              <Button variant='outlined' className={styles.cancel} onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant='contained'
                className={styles.save}
                disabled={!isOwner}
                onClick={handleSave}
              >
                Save
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default VisibilityModal;
